"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AlignmentGeometryFactory = void 0;
var tslib_1 = require("tslib");
var homography_1 = require("../homography");
var vector_1 = require("./../vector");
var Project = tslib_1.__importStar(require("./coordinate.projection"));
var geometry_3d_1 = require("./geometry.3d");
var GeoUtil = tslib_1.__importStar(require("./geometry.util"));
var POINTS = GeoUtil.POINTS;
var PLANE_ORIGIN_POINT = POINTS.TOP_LEFT;
var AlignmentGeometryFactory = /** @class */ (function () {
    function AlignmentGeometryFactory() {
    }
    AlignmentGeometryFactory.prototype.create = function (ctx) {
        var geoCtx = {
            planeCenter_MRW: null,
            planeNormal_MRW: null,
            planeAxis_MP: null,
            planeOrigin_MRW: null,
            focalLength: null,
            homography: null,
            calibration: null,
            ctx: ctx,
            createdBy: geometry_3d_1.Geometry3DCreationMethod.ALIGNMENT_GRID
        };
        if (ctx.calibration) {
            geoCtx.focalLength = ctx.calibration.focal;
            geoCtx.calibration = ctx.calibration;
        }
        else {
            geoCtx.focalLength = ctx.focalLength || GeoUtil.calculateFocalLength(ctx.resolution, ctx.viewAngle);
        }
        geoCtx.planeCenter_MRW = new vector_1.Vector(0, 0, -ctx.distance);
        geoCtx.planeNormal_MRW = AlignmentGeometryFactory.calculatePlaneNormal(ctx, geoCtx);
        geoCtx.planeOrigin_MRW = AlignmentGeometryFactory.calculatePlaneOrigin(ctx, geoCtx);
        geoCtx.planeAxis_MP = AlignmentGeometryFactory.calculateAxisVectors(ctx, geoCtx);
        geoCtx.homography = AlignmentGeometryFactory.calculateHomography(ctx, geoCtx);
        return new geometry_3d_1.Geometry3D(geoCtx);
    };
    AlignmentGeometryFactory.calculatePlaneOrigin = function (ctx, geoCtx) {
        var alignment_CI = ctx.alignment;
        var origin_CI = alignment_CI[PLANE_ORIGIN_POINT];
        return Project.CalibratedToWorld(geoCtx.focalLength, geoCtx.planeCenter_MRW, geoCtx.planeNormal_MRW, ctx.resolution, origin_CI);
    };
    AlignmentGeometryFactory.calculateHomography = function (ctx, geoCtx) {
        var alignment_CI = ctx.alignment;
        var ProjectCalibratedToWorld = Project.CalibratedToWorld.bind(null, geoCtx.focalLength, geoCtx.planeCenter_MRW, geoCtx.planeNormal_MRW, ctx.resolution);
        var ProjectCalibratedToImage = Project.CalibratedToImage.bind(null, ctx.resolution);
        var alignment_MRW = alignment_CI.map(ProjectCalibratedToWorld);
        var dimensions = GeoUtil.estimateDimensions(alignment_MRW);
        var alignment_MP = AlignmentGeometryFactory.CalculateAlignmentPlaneQuad(dimensions);
        var alignment_IP = alignment_CI.map(ProjectCalibratedToImage);
        return homography_1.Homography.create(alignment_IP, alignment_MP);
    };
    AlignmentGeometryFactory.calculatePlaneNormal = function (ctx, geoCtx) {
        var alignment_CI = ctx.alignment;
        var ProjectCalibratedToImage = Project.CalibratedToImage.bind(null, ctx.resolution);
        var alignment_IP = alignment_CI.map(ProjectCalibratedToImage);
        var horIntersection = GeoUtil.lineIntersectionPoint({
            start: alignment_IP[POINTS.BOTTOM_LEFT],
            end: alignment_IP[POINTS.BOTTOM_RIGHT]
        }, {
            start: alignment_IP[POINTS.TOP_LEFT],
            end: alignment_IP[POINTS.TOP_RIGHT]
        });
        var vertIntersection = GeoUtil.lineIntersectionPoint({
            start: alignment_IP[POINTS.BOTTOM_LEFT],
            end: alignment_IP[POINTS.TOP_LEFT]
        }, {
            start: alignment_IP[POINTS.BOTTOM_RIGHT],
            end: alignment_IP[POINTS.TOP_RIGHT]
        });
        var horVanish;
        var vertVanish;
        //This is a special case where the horizontal lines are parallel (they do not intersect at any point)
        if (horIntersection === null) {
            horVanish = new vector_1.Vector(1, 0, 0);
        }
        else {
            horVanish = new vector_1.Vector(horIntersection.x, horIntersection.y, geoCtx.focalLength);
        }
        //This is a special case where the vertical lines are parallel (they do not intersect at any point)
        if (vertIntersection === null) {
            vertVanish = new vector_1.Vector(0, 1, 0);
        }
        else {
            vertVanish = new vector_1.Vector(vertIntersection.x, vertIntersection.y, geoCtx.focalLength);
        }
        var planeNormal_MRW = horVanish.cross(vertVanish);
        var magnitude = planeNormal_MRW.norm();
        //Don't divide by zero please (It just equals -1/12 anyway)
        if (magnitude === 0.0) {
            // We want to return something to make sure we don't implode later calculations
            planeNormal_MRW = new vector_1.Vector(0, 0, 1);
        }
        else {
            planeNormal_MRW = planeNormal_MRW.multiply(1.0 / magnitude);
            // If the plane normal is facing towards us, flip it to ensure that extrusion works as expected
            if (planeNormal_MRW.z < 0.0) {
                planeNormal_MRW = planeNormal_MRW.multiply(-1.0);
            }
        }
        return planeNormal_MRW;
    };
    AlignmentGeometryFactory.calculateAxisVectors = function (ctx, geoCtx) {
        var alignment_CI = ctx.alignment;
        var planeAxis_MP = { x: null, y: null };
        var ProjectCalibratedToWorld = Project.CalibratedToWorld.bind(null, geoCtx.focalLength, geoCtx.planeCenter_MRW, geoCtx.planeNormal_MRW, ctx.resolution);
        var alignment_MRW = alignment_CI.map(ProjectCalibratedToWorld);
        planeAxis_MP.y = alignment_MRW[POINTS.BOTTOM_LEFT].subtract(alignment_MRW[POINTS.TOP_LEFT]);
        var yMagnitude = planeAxis_MP.y.norm();
        planeAxis_MP.x = alignment_MRW[POINTS.BOTTOM_RIGHT].subtract(alignment_MRW[POINTS.BOTTOM_LEFT]);
        var xMagnitude = planeAxis_MP.x.norm();
        // Protect our selves against dividing by zero
        if (xMagnitude !== 0.0 && yMagnitude !== 0.0) {
            planeAxis_MP.x = planeAxis_MP.x.multiply(1.0 / xMagnitude);
            planeAxis_MP.y = planeAxis_MP.y.multiply(1.0 / yMagnitude);
        }
        return planeAxis_MP;
    };
    AlignmentGeometryFactory.CalculateAlignmentPlaneQuad = function (points_RW) {
        var alignment_MP = [];
        //Note in Measurement Plane coordinate (_mpc) space, the Y Axis points down positive. [So that it matches with Raw image]
        alignment_MP[POINTS.TOP_LEFT] = new vector_1.Vector(0, 0);
        alignment_MP[POINTS.TOP_RIGHT] = new vector_1.Vector(points_RW.x, 0);
        alignment_MP[POINTS.BOTTOM_RIGHT] = new vector_1.Vector(points_RW.x, points_RW.y);
        alignment_MP[POINTS.BOTTOM_LEFT] = new vector_1.Vector(0, points_RW.y);
        return alignment_MP;
    };
    return AlignmentGeometryFactory;
}());
exports.AlignmentGeometryFactory = AlignmentGeometryFactory;
//# sourceMappingURL=geometry.factory.alignment.js.map